/**
 * Billing-pool and Stripe-facing endpoints.
 *
 * Includes pricing tiers, saved-card UX, Stripe Elements config, SetupIntent,
 * invoice ledger, billing product catalog, checkout, admin test-credit grants,
 * admin billing snapshot, and wallet policy mutation.
 *
 * Per D-194 the legacy SpendControls endpoint was dropped — no replacement
 * lives here (the wallet ledger is the enforcement boundary).
 */

import { get, post } from './_shared.ts';

// Push 70b (D-198 wallet collapse): updateMemberWalletPolicy retired.
// Wallet policy is CHECK-locked to 'team_pot' at the DB layer per
// Migration 0158; the /api/admin/wallet/policy endpoint was deleted in
// Push 70a. Per-user spend caps live at /api/admin/people/[id]/spend-cap.

/** Push #83 Half A — pricing tier structure for BuyCreditsModal.
 *  Same row that the dispatch path applies markup from — single source
 *  of truth, no hardcoded tile structure in the modal. */
export interface PricingTier {
  dollars: number;
  bonus_pct: number;
  credits: number;
}
// Push #2 (UI Round) D-194 magic abstraction: dropped markup_factor +
// min_markup_factor per FINDING 1.5. SPA never displayed these fields;
// their presence in the response was a magic-abstraction leak.
export interface PricingTiersResponse {
  credits_per_dollar_base: number;
  purchase_tiers: PricingTier[];
  fallback_used?: boolean;
}
export function fetchPricingTiers(): Promise<PricingTiersResponse> {
  return get('/api/pricing/tiers');
}

/** Push #83 Half B — saved-card display + purchase. */
export interface SavedCardResponse {
  has_card: boolean;
  brand?: string;
  last4?: string;
  exp_month?: number | null;
  exp_year?: number | null;
}
export function fetchSavedCard(): Promise<SavedCardResponse> {
  return get('/api/billing/saved-card');
}

/** Push #85 — Stripe Elements client config. */
export interface StripeConfigResponse {
  ok: true;
  publishable_key: string;
  mode: 'test' | 'live';
}
export function fetchStripeConfig(): Promise<StripeConfigResponse> {
  return get('/api/billing/config');
}

/** Push #85 — SetupIntent for embedded card capture. */
export interface SetupIntentResponse {
  ok: true;
  client_secret: string;
  stripe_customer_id: string;
}
export function createSetupIntent(): Promise<SetupIntentResponse> {
  return post('/api/billing/setup-intent', {});
}

/** Push #85 — detach saved card. */
export interface DetachCardResponse {
  ok: true;
  detached: boolean;
  reason?: 'no_card_on_file';
}
export function detachSavedCard(): Promise<DetachCardResponse> {
  return post('/api/billing/detach-card', {});
}

export interface PurchaseSavedCardResponse {
  ok: true;
  payment_intent_id: string | null;
  status: string;
  credits_awarded: number;
  dollars_charged: number;
}
export interface PurchaseRequiresAction {
  ok: false;
  requires_action: true;
  payment_intent_id: string | null;
  client_secret: string | null;
}
export function purchaseSavedCard(
  dollars: number,
): Promise<PurchaseSavedCardResponse | PurchaseRequiresAction> {
  return post('/api/billing/purchase', { dollars });
}

/**
 * Invoice-shaped row from billing__token_grant. Push #80 item C
 * (2026-06-17) widened to include all 5 source values + LEFT JOIN to
 * billing__product for the source-aware pill + product_name column.
 * Post D-189 (Migration D) the product row IS the tier — tier_name and
 * unit_price are kept as NULL-only fields for wire-protocol back-compat;
 * front-ends should read product_name (and the product's dollar_amount via
 * a future widen) for tier identity.
 */
export interface InvoiceRow {
  id: string;
  /** Push 4-1d smoke-fix (2026-07-02): billing__token_grant.short_id
   *  (gnt-prefix). Deep-link target for AdminBilling History rows to
   *  /admin/grants/:short_id (the TokenGrantDetail record page). */
  short_id: string;
  /** 'trial' | 'stripe_purchase' | 'admin_credit' | 'admin_test_mode' | 'referral'
   *  | 'operator_credit' (PLAT-100: an Olytic operator's no-charge grant; not test mode). */
  source: string;
  credits_purchased: string;
  credits_remaining: string;
  granted_at: string;
  granted_by: string | null;
  note: string | null;
  stripe_payment_intent_id: string | null;
  /** Product name from billing__product. NULL when no product stamped. */
  product_name: string | null;
  /** 'credit_pack' | null post D-189; legacy values may persist on older rows. */
  product_kind: string | null;
  /** Retired post D-189. Always NULL — product row IS the tier. */
  tier_name: string | null;
  /** Retired post D-189. Always NULL. */
  unit_price: string | null;
  /** Mirrors source === 'admin_test_mode'. */
  is_test_mode: boolean;
  /** Push #82.6 item H — Stripe-hosted receipt URL captured by webhook
   *  via PaymentIntent expand. NULL for non-stripe sources or if the
   *  webhook-side fetch failed. */
  stripe_receipt_url: string | null;
}

export interface InvoicesResponse {
  phase: 'live';
  invoices: InvoiceRow[];
  /** Backward-compat echo for Round 27 callers. No longer drives the
   *  server-side source filter (all 5 sources always returned). */
  include_test: boolean;
}

export function fetchInvoices(options: { includeTest?: boolean } = {}): Promise<InvoicesResponse> {
  const params = options.includeTest === true ? '?include_test=1' : '';
  return get<InvoicesResponse>(`/api/admin/billing/invoices${params}`);
}

/**
 * GET /api/billing/products — Push #80 item A (refactored for D-189).
 * Catalog of active billing__product rows. Post D-189 pricebook
 * consolidation, each row IS a buyable tier (no nested pricebook_entry
 * indirection). Powers the BuyCreditsModal bundle picker.
 */
export interface BillingProduct {
  id: string;
  name: string;
  kind: 'credit_pack' | string;
  description: string | null;
  /** NUMERIC on the wire as string. Was pricebook_entry.unit_price. */
  dollar_amount: string;
  /** Was pricebook_entry.included_quantity. */
  credits_granted: number;
  /** NUMERIC on the wire as string. NOT NULL DEFAULT 0. */
  bonus_pct: string;
  /** Was pricebook_entry.stripe_price_id. NOT NULL post D-189. */
  stripe_price_id: string;
  /** NULL on all 6 current rows per D-189 Q1 ratification. */
  stripe_product_id: string | null;
  is_active: boolean;
  /** ISO timestamp when tier retired; NULL while active. */
  deactivated_at: string | null;
}

export interface BillingProductsResponse {
  products: BillingProduct[];
}

export function fetchBillingProducts(): Promise<BillingProductsResponse> {
  return get<BillingProductsResponse>('/api/billing/products');
}

/**
 * Buy a credit bundle. Push #81 B3 (refactored for D-189).
 *
 * Calls /api/billing/checkout/token-pack with the billing__product UUID.
 * Post D-189 pricebook consolidation, the product IS the tier — the server
 * reads stripe_price_id + credits_granted + dollar_amount directly from the
 * billing__product row. Returns the Stripe Checkout hosted URL for the
 * caller to redirect to. On STRIPE_ENABLED=false the endpoint returns 503 +
 * code='stripe_not_configured' — the caller (the BuyCreditsModal) catches
 * that to flip to the "Payments coming soon" UX.
 */
export function buyCheckoutTokenPack(
  productId: string,
): Promise<{ ok: true; checkout_url: string; session_id: string }> {
  return post<{ ok: true; checkout_url: string; session_id: string }>(
    '/api/billing/checkout/token-pack',
    { product_id: productId },
  );
}

/**
 * Admin / owner grant test credits to the pool or to a specific user.
 * Push #78 Round 27 — permanent relief-valve for Ean / Josh dogfooding
 * without Stripe. `target_user_id` null → pool grant; set → individual.
 * Server enforces a 100k-per-grant cap.
 */
export function grantTestCredits(args: {
  target_user_id?: string | null;
  credits: number;
  note?: string;
}): Promise<{ ok: true; grant_id: string | null; credits: number; scope: 'pool' | 'individual' }> {
  return post<{ ok: true; grant_id: string | null; credits: number; scope: 'pool' | 'individual' }>(
    '/api/admin/billing/grant-test-credits',
    args,
  );
}

export interface AdminBillingProduct {
  id: string;
  name: string;
  // Pre-smoke close (2026-07-23) — 'seat' union member dropped, matching the
  // Push 79-hygiene tightening applied to AdminBillingSubscription.kind.
  // D-198 (2026-07-17) retired the Platform Seat SKU; Neon `billing__product`
  // has zero 'seat' rows and no writer path creates them. DB CHECK constraint
  // still permits 'seat' (belt-and-suspenders — see Migration 0001), so a
  // rogue manual INSERT could theoretically produce one; treated as
  // operator-visible data drift, not something the SPA should render.
  kind: 'credit_pack';
  is_active: boolean;
  description: string | null;
  stripe_product_id: string | null;
}

export interface AdminBillingAccount {
  cycle_anchor_day: number | null;
  current_cycle_start: string | null;
  current_cycle_end: string | null;
  included_units_this_cycle: number | null;
  consumed_units_this_cycle: number | null;
}

export interface AdminBillingSubscription {
  id: string;
  // Push 79-hygiene (2026-07-23) — 'seat' union member dropped. D-042 v1
  // shipped 'seat' | 'credit_pack'; D-198 (2026-07-17) retired the
  // Platform Seat SKU. The /api/admin/billing endpoint's kind is now
  // CHECK-locked to 'credit_pack' at runtime; the union tracks reality.
  kind: 'credit_pack';
  status: string;
  term_start: string | null;
  term_end: string | null;
  auto_renew: boolean;
  frozen_unit_price: number | null;
  frozen_currency_code: string | null;
  frozen_included_quantity: number | null;
  stripe_subscription_id: string | null;
}

export interface AdminBillingResponse {
  phase: 'test_run' | 'disabled' | 'live';
  message?: string;
  products: AdminBillingProduct[];
  account: AdminBillingAccount | null;
  active_subscriptions: AdminBillingSubscription[];
}

export function fetchAdminBilling(): Promise<AdminBillingResponse> {
  return get<AdminBillingResponse>('/api/admin/billing');
}

// ---------------------------------------------------------------------
// Push 4-1d-o Item C (2026-07-06) — CFO-justification surfaces on
// /admin/billing. Four new endpoints: spend overview card, usage-by-user
// tab, usage-by-capability tab, and the daily time-series chart embedded
// in the Overview tab. All admin-only; RLS scopes to account_id at DB.
// ---------------------------------------------------------------------

/** Range key shared across usage-by-user + usage-by-capability + the
 *  future filters on the History tab. Keep in sync with the same list on
 *  the server endpoints. */
export type BillingRangeKey =
  | 'this_month'
  | 'last_month'
  | 'last_30_days'
  | 'last_90_days'
  | 'all_time';

export const BILLING_RANGE_KEYS: readonly BillingRangeKey[] = [
  'this_month',
  'last_month',
  'last_30_days',
  'last_90_days',
  'all_time',
];

export function rangeLabel(range: BillingRangeKey): string {
  switch (range) {
    case 'this_month':
      return 'This month';
    case 'last_month':
      return 'Last month';
    case 'last_30_days':
      return 'Last 30 days';
    case 'last_90_days':
      return 'Last 90 days';
    case 'all_time':
      return 'All time';
  }
}

/** Response shape for GET /api/admin/billing/spend-overview.
 *  Numeric credit totals are strings on the wire (NUMERIC in Postgres);
 *  the front-end formats with Number() + toLocaleString(). trend_pct is
 *  null when last month's spend is 0 (division-by-zero would be a NaN
 *  leak). */
export interface SpendOverviewResponse {
  balance_credits: string;
  spend_this_month_credits: string;
  spend_last_month_credits: string;
  trend_pct: number | null;
  forecast_month_end_credits: string;
  // Sub-agent UUUUU (2026-07-31) — prior-period comparison metrics so
  // every KPI tile on Analytics Overview carries a ±% trend chip.
  dispatches_this_month: number;
  dispatches_last_month: number;
  active_users_this_month: number;
  active_users_last_month: number;
  top_spender: {
    account_user_id: string;
    short_id: string;
    display_name: string;
    spend_credits: string;
  } | null;
  top_capability: {
    kind: 'connector' | 'model' | 'prompt';
    short_id: string;
    display_name: string;
    spend_credits: string;
  } | null;
}

export function fetchSpendOverview(): Promise<SpendOverviewResponse> {
  return get<SpendOverviewResponse>('/api/admin/billing/spend-overview');
}

export interface UsageByUserRow {
  account_user_id: string;
  short_id: string;
  display_name: string;
  email: string;
  role: 'owner' | 'admin' | 'user';
  credits_used_period: string;
  credits_used_all_time: string;
  dispatches_period: number;
  last_activity_at: string | null;
}

export interface UsageByUserResponse {
  range: BillingRangeKey;
  range_label: string;
  rows: UsageByUserRow[];
}

export function fetchUsageByUser(
  range: BillingRangeKey = 'this_month',
): Promise<UsageByUserResponse> {
  return get<UsageByUserResponse>(
    `/api/admin/billing/usage-by-user?range=${encodeURIComponent(range)}`,
  );
}

export type CapabilityKindFilter = 'connector' | 'model' | 'prompt' | 'all';

export interface UsageByCapabilityRow {
  kind: 'connector' | 'model' | 'prompt';
  instance_short_id: string;
  display_name: string;
  template_display_name: string;
  dispatches_period: number;
  credits_used_period: string;
  avg_cost_per_dispatch: string;
}

export interface UsageByCapabilityResponse {
  range: BillingRangeKey;
  range_label: string;
  kind: CapabilityKindFilter;
  rows: UsageByCapabilityRow[];
}

export function fetchUsageByCapability(
  range: BillingRangeKey = 'this_month',
  kind: CapabilityKindFilter = 'all',
): Promise<UsageByCapabilityResponse> {
  const params = new URLSearchParams({
    range,
    kind,
  });
  return get<UsageByCapabilityResponse>(
    `/api/admin/billing/usage-by-capability?${params.toString()}`,
  );
}

// ---------------------------------------------------------------------
// Push 70c (D-198 wallet collapse, 2026-07-17) — per-tag spend rollup.
// Consumes billing__token_debit.attributed_tags (Migration 0157 snapshot
// column). Multi-tag users' spend counts once PER tag they carried at
// debit time. Debits with NULL / empty attributed_tags aggregate into a
// single "(no tag)" bucket — server returns the sentinel string
// UNTAGGED_TAG_MARKER for that row so the client can render "(no tag)".
// See WALLET-COLLAPSE-PLAN.md § 8.1 for the aggregation shape.
// ---------------------------------------------------------------------

/**
 * Server-side sentinel string for the untagged bucket. Chosen to be
 * unlikely to collide with a real tag ("__untagged__" — Postgres would
 * store a literal tag by that name distinctly on typing). Callers should
 * check `row.tag === UNTAGGED_TAG_MARKER` to render "(no tag)".
 */
export const UNTAGGED_TAG_MARKER = '__untagged__';

export interface UsageByTagRow {
  tag: string;
  credits_used_period: string;
  dispatches_period: number;
  distinct_user_count: number;
}

export interface UsageByTagResponse {
  range: BillingRangeKey;
  range_label: string;
  rows: UsageByTagRow[];
}

export function fetchUsageByTag(
  range: BillingRangeKey = 'this_month',
): Promise<UsageByTagResponse> {
  const params = new URLSearchParams({ range });
  return get<UsageByTagResponse>(
    `/api/admin/billing/usage-by-tag?${params.toString()}`,
  );
}

export type SpendTimeseriesWindow = '30d' | '90d';

export interface SpendTimeseriesPoint {
  date: string;
  total_credits: string;
  connector_credits?: string;
  model_credits?: string;
  prompt_credits?: string;
}

export interface SpendTimeseriesResponse {
  window: SpendTimeseriesWindow;
  stacked: boolean;
  points: SpendTimeseriesPoint[];
}

/** Push 4-1ad (2026-07-09) — optional record-scope filters so the
 *  Sparkline primitive on PersonDetail / AdminConnectorDetail /
 *  AdminModelDetail can fetch a scoped series. Server validates each
 *  UUID with UUID_RE and silently ignores malformed values; filters
 *  compose (AND). Undefined / empty values are simply omitted from
 *  the query string so default (account-wide) behavior is preserved. */
export interface SpendTimeseriesFilters {
  userId?: string;
  connectorInstanceId?: string;
  modelInstanceId?: string;
}

export function fetchSpendTimeseries(
  window: SpendTimeseriesWindow = '30d',
  stacked = false,
  filters: SpendTimeseriesFilters = {},
): Promise<SpendTimeseriesResponse> {
  const params = new URLSearchParams({
    window,
    stacked: stacked ? 'true' : 'false',
  });
  if (filters.userId !== undefined && filters.userId !== '') {
    params.set('user_id', filters.userId);
  }
  if (
    filters.connectorInstanceId !== undefined &&
    filters.connectorInstanceId !== ''
  ) {
    params.set('connector_instance_id', filters.connectorInstanceId);
  }
  if (filters.modelInstanceId !== undefined && filters.modelInstanceId !== '') {
    params.set('model_instance_id', filters.modelInstanceId);
  }
  return get<SpendTimeseriesResponse>(
    `/api/admin/billing/spend-timeseries?${params.toString()}`,
  );
}

// ---------------------------------------------------------------------
// Push 5-A (2026-07-11) — D-196 activity-count series for capability
// detail pages that aren't models. Complements fetchSpendTimeseries
// (which aggregates billing__token_debit credits, AI-only per D-196).
// This helper aggregates sys__event_log row counts — the honest "Runs"
// surface for connectors / prompts / documents / folders.
//
// Push 5-A-cont (2026-07-11): vocabulary swept from "usage" → "activity"
// per D-196 sub-clause. "Usage" is money-loaded in ambient SaaS
// vocabulary; "activity" is the free-event lens. Wire field renamed
// `uses` → `runs`; response type + fetch helper renamed accordingly.
// The underlying sys__event_log DB shape is unchanged.
// ---------------------------------------------------------------------

export interface ActivityTimeseriesPoint {
  date: string;
  runs: string;
}

export interface ActivityTimeseriesResponse {
  window: SpendTimeseriesWindow;
  points: ActivityTimeseriesPoint[];
  total_runs_30d: string;
}

export interface ActivityTimeseriesFilters {
  /** The capability instance UUID keyed by sys__event_log.target_id.
   *  Common shape for connectors + prompts + models. */
  targetId?: string;
  /** Alternative address for events keyed by primitive_instance_id
   *  (e.g. documents pre-Push 5-B where target_kind was 'n/a'). */
  primitiveInstanceId?: string;
  /** Recommended AND filter — event log target_kind (connector_instance /
   *  prompt_instance / model_instance / document / etc.). */
  targetKind?: string;
}

export function fetchActivityTimeseries(
  window: SpendTimeseriesWindow = '30d',
  filters: ActivityTimeseriesFilters,
): Promise<ActivityTimeseriesResponse> {
  const params = new URLSearchParams({ window });
  if (filters.targetId !== undefined && filters.targetId !== '') {
    params.set('target_id', filters.targetId);
  }
  if (
    filters.primitiveInstanceId !== undefined &&
    filters.primitiveInstanceId !== ''
  ) {
    params.set('primitive_instance_id', filters.primitiveInstanceId);
  }
  if (filters.targetKind !== undefined && filters.targetKind !== '') {
    params.set('target_kind', filters.targetKind);
  }
  return get<ActivityTimeseriesResponse>(
    `/api/admin/capabilities/activity-timeseries?${params.toString()}`,
  );
}

// ---------------------------------------------------------------------
// Push 4-1d-p Item 2 (2026-07-06) — /admin/billing/transactions extended
// (was /admin/billing?tab=history pre-Push-4-1f).
// UNION over billing__token_grant + billing__token_debit with user +
// capability + range filters. Distinct from fetchInvoices (which stays
// live for the Buy tab's "Recent purchases" mini-list, grants-only).
// ---------------------------------------------------------------------

export type BillingHistoryCapabilityKind = 'connector' | 'model';

export interface BillingHistoryRow {
  row_type: 'grant' | 'debit';
  short_id: string;
  credits: string;
  happened_at: string;
  user_display_name: string | null;
  user_short_id: string | null;
  capability_kind: BillingHistoryCapabilityKind | null;
  capability_display_name: string | null;
  capability_short_id: string | null;
  /** grant.source ('stripe_purchase' | 'admin_credit' | 'admin_test_mode'
   *  | 'trial' | 'referral' | 'operator_credit') OR NULL for debits. Mirrors the History tab
   *  source pill contract. */
  source: string | null;
}

export interface BillingHistoryCapability {
  kind: BillingHistoryCapabilityKind;
  short_id: string;
  display_name: string;
  /** capability_target = `${kind}:${short_id}`. Pass this whole value as
   *  the ?capability_target= query param. Denormalized on the wire so the
   *  SPA never has to reconstruct it. */
  target_value: string;
}

export interface BillingHistoryUser {
  user_id: string;
  short_id: string;
  display_name: string;
}

export interface BillingHistoryResponse {
  range: BillingRangeKey;
  range_label: string;
  filters: {
    user_id: string | null;
    capability_target: string | null;
  };
  rows: BillingHistoryRow[];
  users: BillingHistoryUser[];
  capabilities: BillingHistoryCapability[];
}

export function fetchBillingHistory(opts: {
  range?: BillingRangeKey;
  userId?: string | null;
  capabilityTarget?: string | null;
} = {}): Promise<BillingHistoryResponse> {
  const params = new URLSearchParams();
  params.set('range', opts.range ?? 'this_month');
  if (opts.userId !== null && opts.userId !== undefined && opts.userId !== '') {
    params.set('user_id', opts.userId);
  }
  if (
    opts.capabilityTarget !== null &&
    opts.capabilityTarget !== undefined &&
    opts.capabilityTarget !== ''
  ) {
    params.set('capability_target', opts.capabilityTarget);
  }
  return get<BillingHistoryResponse>(
    `/api/admin/billing/history?${params.toString()}`,
  );
}

// ---------------------------------------------------------------------
// LowBalance banner threshold config.
//
// Push 70b (D-198 wallet collapse): standard-user banner + per-user
// threshold override retired. Single workspace-scope knob remains:
// sys__account.settings.low_balance_days (default 7). Admins/owners see
// the banner; standard users never do (they can't top up anyway).
// ---------------------------------------------------------------------

export interface LowBalanceConfigResponse {
  /** Days-of-runway threshold for the admin/owner banner. null = uses default. */
  low_balance_days: number | null;
  /** Same default the wallet endpoint clamps to. */
  defaults: { low_balance_days: number };
  bounds: { min_days: number; max_days: number };
}

export function fetchLowBalanceConfig(): Promise<LowBalanceConfigResponse> {
  return get<LowBalanceConfigResponse>(
    '/api/admin/workspace/low-balance-config',
  );
}

/** PUT — number saves a value, explicit null clears (revert to default). */
export function updateLowBalanceConfig(patch: {
  low_balance_days?: number | null;
}): Promise<{ ok: true; patch: Record<string, number | null> }> {
  return fetch('/api/admin/workspace/low-balance-config', {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  }).then(async (res) => {
    const text = await res.text();
    let parsed: unknown = null;
    try {
      parsed = text === '' ? null : JSON.parse(text);
    } catch {
      parsed = null;
    }
    if (!res.ok) {
      const msg =
        parsed && typeof parsed === 'object' && parsed !== null && 'detail' in parsed
          ? String((parsed as { detail: unknown }).detail)
          : `PUT /api/admin/workspace/low-balance-config failed (${res.status})`;
      throw new Error(msg);
    }
    return parsed as { ok: true; patch: Record<string, number | null> };
  });
}
